import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import type { AudienceResponse } from '@troia-v3/types';
import { audienceStatusMeta, audienceTypeMeta, formatEntries } from './audiencePresentation';

/**
 * Linha de tabela de público (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + coluna multi-linha (ícone + nome + descrição) +
 * menu ••• (última col). Cores via CSS variables da página.
 */
export interface AudienceListItemProps {
  audience: AudienceResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const AudienceListItem: React.FC<AudienceListItemProps> = ({
  audience,
  selected = false,
  onToggleSelect,
  onView,
  onDelete,
}) => {
  const type = audienceTypeMeta[audience.identifierType];
  const status = audienceStatusMeta[audience.processingStatus] ?? audienceStatusMeta.idle;

  const actions: CardAction[] = [];
  if (onView) actions.push({ label: 'Ver contatos', icon: 'user', onClick: () => onView(audience.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(audience.id), variant: 'danger' });

  return (
    <tr
      className="border-b transition-colors"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`audience-row-${audience.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(audience.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`audience-check-${audience.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Nome: ícone-categoria + nome + descrição */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
          >
            <Icon name={type.icon} className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <Link
              to={`/prospecting/audiences/${audience.id}`}
              className="font-semibold text-sm truncate block hover:underline"
              style={{ color: 'var(--theme-card-text)' }}
              data-testid={`audience-name-${audience.id}`}
            >
              {audience.name}
            </Link>
            {audience.description && (
              <div className="text-xs truncate max-w-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {audience.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Tipo */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name={type.icon} className="w-3 h-3" />
          {type.label}
        </span>
      </td>

      {/* Entradas */}
      <td className="px-4 py-3">
        <span className="text-sm tabular-nums" style={{ color: 'var(--theme-card-text)', fontWeight: 700 }}>
          {formatEntries(audience.totalEntries)}
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusPill
          variant={status.variant}
          label={status.label}
          data-testid={`audience-status-${audience.id}`}
        />
      </td>

      {/* Criado em */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        {new Date(audience.createdAt).toLocaleDateString('pt-BR')}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`audience-actions-${audience.id}`} />
      </td>
    </tr>
  );
};

AudienceListItem.displayName = 'AudienceListItem';
